// src/context/FontContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const FontContext = createContext(null);

export function FontProvider({ children }) {
  const { user } = useAuth(); // Get auth state
  const [font, setFont] = useState("Helvetica");
  const [loading, setLoading] = useState(true);

  // Apply font to DOM
  const applyFont = (fontName) => {
    const fontFamily = fontName === "sans-serif" ? "sans-serif" : `'${fontName}', sans-serif`;
    document.documentElement.style.setProperty("--ess-font", fontFamily);
    document.body.style.fontFamily = fontFamily;
  };

  // Fetch font from backend - ONLY when user is authenticated
  const fetchFont = async () => {
    try {
      const response = await api.get("/settings/font");
      const savedFont = response.data?.font_family || "Helvetica";
      console.log("📦 Font from backend:", savedFont);
      setFont(savedFont);
      applyFont(savedFont);
    } catch (e) {
      console.error("❌ Failed to fetch font:", e.response?.status, e.response?.data);
      applyFont("Helvetica");
    } finally {
      setLoading(false);
    }
  };

  // Only fetch when user exists (authenticated)
  useEffect(() => {
    if (user) {
      fetchFont();
    } else {
      // Not authenticated yet, keep loading true
      setLoading(true);
    }
  }, [user]); // Re-run when user state changes

  // Save font to backend
  const updateFont = async (fontName) => {
    try {
      await api.put("/settings/font", { font_family: fontName });
      setFont(fontName);
      applyFont(fontName);
      return true;
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to save font setting");
      return false;
    }
  };

  return (
    <FontContext.Provider value={{ font, loading, updateFont, refreshFont: fetchFont }}>
      {children}
    </FontContext.Provider>
  );
}

export function useFont() {
  const context = useContext(FontContext);
  if (!context) {
    throw new Error("useFont must be used within a FontProvider");
  }
  return context;
}
